/// <reference path="koyori.ts" />
/// <reference path="fetch.ts" />
/// <reference path="input_file.ts" />
/// <reference path="input_text.ts" />
/// <reference path="input_textarea.ts" />
/// <reference path="select.ts" />
/// <reference path="checkbox.ts" />
/// <reference path="animation.ts" />

/**
 * 入力項目を定義から生成します。
 * タグ内に記述されているエレメントは入力項目の下部に配置されます。
 * <dl>
 * <dt>属性</dt>
 * <dd>definition: 入力項目の定義をJSON形式で指定します</dd>
 * <dd>definition-url: 入力項目の定義を取得するURLです</dd>
 * <dd>insert-only-disable: inputOnlyの項目をdisabledにします</dd>
 * <dd>action: フォームがsubmitされた際のデータ送信先URL</dd>
 * <dd>fetch-success: AJAX通信が成功した場合に遷移するURL</dd>
 * <dd>method: HTTPメソッド（デフォルトは"GET"）
 * <dd>enctype: MIMEタイプ
 * <dd>fetch: 送信をAJAXとする</dd>
 * <dd>default-value-url: 入力項目の初期値の取得先URL</dd>
 * </dl>
 * <dl>
 * <dt>イベント</dt>
 * <dd>fetch-approach: AJAX通信が発生する前</dd>
 * <dd>fetch-success: AJAX通信が成功した場合</dd>
 * <dd>fetch-ng: AJAX通信が成功したがステータスコードが異常な場合</dd>
 * <dd>default-fetch-success: AJAXでの初期値取得が成功した場合</dd>
 * <dd>fetch-error: AJAX通信に失敗した場合</dd>
 * <dd>update: フォームに値が設定された場合</dd>
 * </dl>
 */
class KoyoriForm extends KoyoriElement {
  static listCount = 0;

  static get observedAttributes() {
    return [
      'definition',
      'definition-url',
      'action',
      'method',
      'enctype',
      'default-value-url',
    ];
  }

  /**
   * @param name 型名
   * @return 型
   */
  static getTypeByName(
    types: KoyoriFormDefinitionType[],
    name: string
  ): KoyoriFormDefinitionType {
    let result = null;
    types.forEach((type) => {
      if (type.name == name) {
        result = type;
      }
    });
    return result;
  }

  /**
   * @param definition
   * @param properties
   * @param name
   * @return プロパティ
   */
  static getPropertyByName(
    definition: KoyoriFormDefinition,
    properties: KoyoriFormDefinitionTypeProperty[],
    name: string
  ): KoyoriFormDefinitionTypeProperty {
    let point = name.indexOf('.');
    if (point > 0) {
      let parentName = name.substring(0, point);
      let property = this.getPropertyByName(definition, properties, parentName);
      let childType = this.getTypeByName(definition.types, property.type);
      return this.getPropertyByName(
        definition,
        childType.properties,
        name.substring(point + 1)
      );
    }

    let result = null;
    properties.forEach((type) => {
      if (type.name == name) {
        result = type;
      }
    });
    return result;
  }

  /** 内部フォームエレメント */
  private innerForm: HTMLFormElement;

  /** 既存の内部エレメント */
  private defaultElements: HTMLElement[];

  /** 入力定義 */
  private def: KoyoriFormDefinition;

  constructor() {
    super();

    window.addEventListener('resize', () => {
      this.updateLabelWidth();
    });
  }

  connectedCallback(): void {
    this.defaultElements = KoyoriElement.children(this);

    this.innerForm = <HTMLFormElement>this.querySelector('.koy-form-inner');
    if (this.innerForm == null) {
      this.innerForm = document.createElement('form');
      this.innerForm.classList.add('koy-form-inner');
      this.appendChild(this.innerForm);
      let messages = document.createElement('div');
      messages.classList.add('koy-form-messages');
      messages.style.display = 'none';
      this.innerForm.appendChild(messages);
      this.defaultElements.forEach((element) => {
        element.classList.add('koy-form-default');
        this.innerForm.appendChild(element);
      });
      this.innerForm.addEventListener('submit', (event) => {
        if (
          this.getAttribute('fetch') == null ||
          this.getAttribute('action') == null
        ) {
          return;
        }
        event.preventDefault();
        event.stopPropagation();
        var result = KoyoriElement.fireEvent(this, 'submit');
        if (result == false) {
          return;
        }
        let url = this.getAttribute('action');
        let method = this.getAttribute('method') || 'GET';
        let body = undefined;
        let enctype =
          this.getAttribute('enctype') || 'application/x-www-form-urlencoded';
        if (enctype.indexOf('json') > 0) {
          if (method == 'GET') {
            url = url + '?' + this.getValuesAsJson();
          } else {
            body = this.getValuesAsJson();
          }
        } else {
          if (method == 'GET') {
            url = url + this.getValuesAsQueryString();
          } else {
            let params = new URLSearchParams();
            let object = this.getValuesAsObject();
            Object.keys(object).forEach((key) => {
              params.set(key, object[key]);
            });
            body = params.toString();
          }
        }
        KoyoriFetch.fetch(
          url,
          {
            method: method,
            headers: {
              'Content-Type': enctype,
            },
            body: body,
            credentials: 'include',
            mode: 'cors',
          },
          {
            approach: () => {
              this.clearAllMessages();
              return true;
            },
            success: (response) => {
              let successUrl = this.getAttribute('fetch-success');
              let responseContentType = response.headers.get('Content-Type');
              if (
                responseContentType != null &&
                responseContentType.indexOf('json') > 0
              ) {
                response.json().then((data) => {
                  if (data['STATUS'] != undefined && data['STATUS'] != 200) {
                    if (data['STATUS'] == 400) {
                      for (let name in data) {
                        if (name == 'STATUS') {
                          continue;
                        }
                        this.addErrorMessage(name, data[name]);
                      }
                    }
                    KoyoriElement.fireEvent(this, 'fetch-ng', true, true, {
                      status: data.RESPONSE,
                      response: response,
                    });
                  } else {
                    KoyoriElement.fireEvent(this, 'fetch-success', true, true, {
                      response: response,
                      data: data,
                    });
                    if (successUrl != null) {
                      location.href = successUrl;
                    }
                  }
                });
              } else {
                response.text().then((text) => {
                  KoyoriElement.fireEvent(this, 'fetch-success', true, true, {
                    response: response,
                    text: text,
                  });
                  if (successUrl != null) {
                    location.href = successUrl;
                  }
                });
              }
              return false;
            },
            ng: (response) => {
              let responseContentType = response.headers.get('Content-Type');
              if (
                response.status == 400 &&
                responseContentType != null &&
                responseContentType.indexOf('json') > 0
              ) {
                response.json().then((data) => {
                  for (let name in data) {
                    this.addErrorMessage(name, data[name]);
                  }
                });
              }
              KoyoriElement.fireEvent(this, 'fetch-ng', true, true, {
                status: response.status,
                response: response,
              });
              return false;
            },
            error: (response, error) => {
              KoyoriElement.fireEvent(this, 'fetch-error', true, true, {
                response: response,
                error: error,
              });
              return false;
            },
          },
          this
        );
      });
    }

    super.connectedCallback();

    let dialog = this.closest('koy-dialog');
    if (dialog != null) {
      dialog.addEventListener('open', () => {
        this.updateLabelWidth();
      });
    } else {
      this.updateLabelWidth();
    }
  }

  set definition(def: string) {
    this.setAttribute('definition', def);
  }

  /**
   * 入力エレメントの値をvalue属性の値に設定します。
   */
  reset(): void {
    KoyoriElement.querySelectorAll(this.innerForm, '[name]').forEach(
      (input) => {
        if (eval('typeof input.reset == "function"')) {
          eval('input.reset()');
          return;
        }
        let value;
        if (input instanceof KoyoriCheckbox) {
          if (input.getAttribute('checked') == null) {
            value = false;
          } else {
            value = true;
          }
        } else {
          value = input.getAttribute('value');
          if (value == null) {
            value = '';
          }
        }
        this.setValue(input, value);
      }
    );
  }

  /**
   * フォームをサブミットします。
   */
  submit(): void {
    KoyoriElement.fireEvent(this.innerForm, 'submit');
  }

  /**
   * 入力された値をJSONデータとして取得します。
   *
   * @param space インデント
   * @return 入力値のJSONデータ
   */
  getValuesAsJson(space = 0): string {
    return JSON.stringify(this.getValuesAsObject(), null, space);
  }

  /**
   * 入力された値をObjectとして取得します。
   */
  getValuesAsObject(): any {
    let values: any = {};
    KoyoriElement.querySelectorAll(this.innerForm, '[name]').forEach(
      (input) => {
        if (
          input.classList.contains('koy-input') == false &&
          input.closest('.koy-input') != null
        ) {
          return;
        }
        let value = this.getValueFromElement(input);
        if (value === null || value === undefined || value === '') {
          return;
        }
        let name = input.getAttribute('name');
        if (name == null) {
          return;
        }
        if (name.match('^.*\\[\\]$')) {
          value = [value];
          name = name.substring(0, name.length - 2);
        }
        let names = name.split('.');
        let target = values;
        for (let i = 0; i < names.length - 1; i++) {
          if (target[names[i]] == undefined) {
            target[names[i]] = {};
          }
          target = target[names[i]];
        }
        if (Array.isArray(value)) {
          if (input instanceof KoyoriInputFile) {
            // TODO InputFileのために暫定的に配列対応
            values[names[names.length - 2]] = new Array();
            value.forEach((part) => {
              let object: KoyoriFormHash = {};
              object[names[names.length - 1]] = part;
              values[names[names.length - 2]].push(object);
            });
          } else {
            if (Array.isArray(target[names[names.length - 1]])) {
              target[names[names.length - 1]].push(value[0]);
            } else {
              target[names[names.length - 1]] = value;
            }
          }
        } else {
          target[names[names.length - 1]] = value;
        }
      }
    );
    return values;
  }

  /**
   * 入力された値をクエリストリングとして取得します。
   *
   * @return クエリストリング
   */
  getValuesAsQueryString(): string {
    let values = '';
    KoyoriElement.querySelectorAll(this.innerForm, '[name]').forEach(
      (input) => {
        if (
          input.classList.contains('koy-input') == false &&
          input.closest('.koy-input') != null
        ) {
          return;
        }
        let value = this.getValueFromElement(input);
        if (value === undefined) {
          return;
        }
        let name = input.getAttribute('name');
        if (values.length == 0) {
          values = '?';
        } else {
          values += '&';
        }
        values += encodeURIComponent(name) + '=' + encodeURIComponent(value);
      }
    );
    return values;
  }

  /**
   * 入力された値を連想配列として取得します。
   *
   * @return 連想配列
   */
  getValuesAsHash(): any {
    let values: any = {};
    KoyoriElement.querySelectorAll(this.innerForm, '[name]').forEach(
      (input) => {
        if (
          input.classList.contains('koy-input') == false &&
          input.closest('.koy-input') != null
        ) {
          return;
        }
        let value = this.getValueFromElement(input);
        if (value === undefined) {
          return;
        }
        let name = input.getAttribute('name');
        values[name] = value;
      }
    );
    return values;
  }

  /**
   * 入力された値を取得します。
   *
   * @param input
   */
  private getValueFromElement(input: HTMLElement | string): any {
    if (typeof input == 'string') {
      input = <HTMLElement>this.querySelector('[name="' + input + '"]');
    }
    let value;
    if (input instanceof KoyoriCheckbox) {
      let checkbox = <KoyoriCheckbox>input;
      if (
        checkbox.getAttribute('value') == null ||
        checkbox.getAttribute('value') == 'true'
      ) {
        if (checkbox.checked) {
          value = true;
        } else {
          value = false;
        }
      } else {
        if (checkbox.checked) {
          value = checkbox.value;
        }
      }
    } else if (input instanceof KoyoriInputFile) {
      let inputFile = <KoyoriInputFile>input;
      if (inputFile.getAttribute('multiple') == null) {
        value = (<KoyoriInputFile>input).value;
      } else {
        value = (<KoyoriInputFile>input).values;
      }
    } else if (input instanceof KoyoriSelect) {
      let inputFile = <KoyoriSelect>input;
      if (inputFile.getAttribute('multiple') == null) {
        value = (<KoyoriSelect>input).value;
      } else {
        value = (<KoyoriSelect>input).values;
      }
    } else {
      value = eval('input.value');
    }
    return value;
  }

  /**
   * 値を設定します。
   *
   * @param values 値オブジェクト
   */
  setValues(values: any): void {
    let isUpdate = false;
    KoyoriElement.querySelectorAll(this.innerForm, '[name]').forEach(
      (input) => {
        if (
          input.classList.contains('koy-input') == false &&
          input.closest('.koy-input') != null
        ) {
          return;
        }
        let name = input.getAttribute('name');
        if (name == null) {
          return;
        }
        let parent = (<HTMLElement>input).closest('.koy-input');
        if (parent != null) {
          input = <HTMLElement>parent;
        }
        if (
          input.getAttribute('readonly') != null &&
          this.getValueFromElement(input)
        ) {
          // 値が設定されておりreadonly属性がある場合は値を設定しない
          return;
        }

        let value = values[name];
        if (value != undefined) {
          let result = this.setValue(input, value);
          if (result) {
            isUpdate = true;
          }
          return;
        }

        let names = name.split('.');
        let target = values;
        for (let i = 0; i < names.length - 1; i++) {
          if (target[names[i]] == undefined) {
            return;
          }
          target = target[names[i]];
        }
        let result = this.setValue(input, target[names[names.length - 1]]);
        if (result) {
          isUpdate = true;
        }
      }
    );
    if (isUpdate) {
      KoyoriElement.fireEvent(this, 'update', true, true, values);
    }
  }

  /**
   * 値を設定します。
   *
   * @param values クエリストリング
   */
  setValuesAsQueryString(values: string): void {
    if (values.indexOf('?') == 0) {
      values = values.substring(1);
    }
    let object: KoyoriFormHash = {};
    values.split('&').forEach((keyValue) => {
      let point = keyValue.indexOf('=');
      if (point < 0) {
        return;
      }
      let key = keyValue.substring(0, point);
      let value = keyValue.substring(point + 1);
      object[key] = value;
    });
    this.setValues(object);
  }

  /**
   * フォーム内のメッセージをすべて削除します。
   */
  clearAllMessages(): void {
    KoyoriElement.querySelectorAll(this.innerForm, '[name]').forEach(
      (input) => {
        input.removeAttribute('state');
        input.removeAttribute('message');
      }
    );
    let messages = <HTMLElement>(
      this.innerForm.querySelector('.koy-form-messages')
    );
    messages.style.display = 'none';
    messages.textContent = '';
  }

  /**
   * フォーム内にメッセージを追加します。
   * nameに一致する入力エレメントが存在しない場合はメッセージエリアに表示します。
   *
   * @param name エレメントのname属性の値
   * @param message メッセージ文字列
   */
  addErrorMessage(name: string, message: string): void {
    let find = false;
    KoyoriElement.querySelectorAll(
      this.innerForm,
      '[name="' + name + '"]'
    ).forEach((input) => {
      find = true;
      input.setAttribute('state', 'error');
      input.setAttribute('message', message);
    });
    if (find == false) {
      let messages = <HTMLElement>(
        this.innerForm.querySelector('.koy-form-messages')
      );
      let div = document.createElement('div');
      div.textContent = message;
      messages.appendChild(div);
      KoyoriAnimation.fadeIn(messages);
    }
  }

  /**
   * 再レイアウトを行います。
   */
  reLayout(): void {
    this.updateLabelWidth();
  }

  /**
   * 値を設定します。値が変更された場合はchangeイベントが発行されます。
   *
   * @param input
   * @param value
   * @param 値が変更された場合はtrue
   */
  private setValue(input: HTMLElement | String, value: any): boolean {
    if (value == undefined || value == null) {
      return false;
    }
    if (typeof input == 'string') {
      input = <HTMLElement>this.querySelector('[name="' + input + '"]');
      let parent = (<HTMLElement>input).closest('.koy-input');
      if (parent != null) {
        input = <HTMLElement>parent;
      }
    }
    let current = this.getValueFromElement(<HTMLElement>input);
    if (typeof current == 'boolean' && typeof value == 'string') {
      value = value == 'true' ? true : false;
    }
    if (current === value) {
      return false;
    }
    if (typeof value == 'boolean') {
      if (value == true) {
        eval('input.checked = true');
      } else {
        eval('input.checked = false');
      }
    } else if (Array.isArray(value)) {
      eval('input.values = ' + JSON.stringify(value));
    } else {
      eval('input.value = ' + JSON.stringify(value));
    }
    KoyoriElement.fireEvent(<HTMLElement>input, 'change', true, true, value);
    return true;
  }

  protected definitionChanged(value: string): void {
    this.def = <KoyoriFormDefinition>JSON.parse(value);
    this.updateDefinition();
  }

  protected definitionUrlChanged(value: string): void {
    KoyoriFetch.fetch(
      value,
      null,
      {
        success: (response) => {
          response.json().then((data) => {
            if (data == null) {
              return;
            }
            this.def = data;
            this.updateDefinition();
          });
          return true;
        },
      },
      this
    );
  }

  private updateDefinition() {
    this.innerForm.innerHTML = '';
    this.addElement(
      this.innerForm,
      '',
      KoyoriForm.getTypeByName(this.def.types, this.def.type)
    );
    this.defaultElements.forEach((element) => {
      if (element.getAttribute('name') == null) {
        this.innerForm.appendChild(element);
      }
    });
    this.updateLabelWidth();
    this.updateState();
  }

  protected actionChanged(value: string): void {
    if (value == null) {
      this.innerForm.removeAttribute('action');
    } else {
      this.innerForm.setAttribute('action', value);
    }
  }

  protected methodChanged(value: string): void {
    if (value == null) {
      this.innerForm.removeAttribute('method');
    } else {
      this.innerForm.setAttribute('method', value);
    }
  }

  protected enctypeChanged(value: string): void {
    if (value == null) {
      this.innerForm.removeAttribute('enctype');
    } else {
      this.innerForm.setAttribute('enctype', value);
    }
  }

  protected defaultValueUrlChanged(value: string): void {
    if (value == null) {
      return;
    }
    KoyoriFetch.fetch(
      value,
      {
        credentials: 'include',
        mode: 'cors',
      },
      {
        success: (response) => {
          response.json().then((data) => {
            this.setValues(data);
            KoyoriElement.fireEvent(this, 'default-fetch-success', true, true, {
              response: response,
              data: data,
            });
          });
          return false;
        },
      },
      this
    );
  }

  private addElement(
    parent: HTMLElement,
    namePrefix: string,
    defType: KoyoriFormDefinitionType
  ): void {
    defType.properties.forEach((property) => {
      let element: HTMLElement = null;
      let name = namePrefix + property.name;
      this.defaultElements.forEach((defaultElement) => {
        if (element == null && defaultElement.getAttribute('name') == name) {
          element = defaultElement;
        }
      });
      if (element == null) {
        switch (property.type) {
          case 'text':
          case 'kana':
          case 'secret':
          case 'number':
          case 'date':
          case 'time':
          case 'datetime':
          case 'tel':
          case 'email':
          case 'url':
            if (property.candidates) {
              element = new KoyoriSelect();
              property.candidates.forEach((candidate) => {
                let option = document.createElement('option');
                option.setAttribute(
                  'value',
                  candidate.value != undefined
                    ? candidate.value
                    : candidate.label
                );
                option.innerText = candidate.label;
                element.appendChild(option);
              });
              (<KoyoriSelect>element).rebuild();
            } else {
              switch (property.type) {
                case 'text':
                  if (property.multiLine) {
                    element = new KoyoriInputTextarea();
                  } else {
                    element = new KoyoriInputText();
                  }
                  break;
                case 'secret':
                  element = new KoyoriInputText();
                  element.setAttribute('secret', '');
                  break;
                case 'kana':
                  element = new KoyoriInputText();
                  element.setAttribute('inputmode', 'kana');
                  break;
                case 'number':
                  element = new KoyoriInputText();
                  element.setAttribute('inputmode', 'numeric');
                  if (!property.pattern) {
                    element.setAttribute('pattern', '^(-?[0-9]+(.[0-9]+)?)?$');
                  }
                  break;
                case 'date':
                  element = new KoyoriInputText(); // TODO 変更する
                  break;
                case 'time':
                  element = new KoyoriInputText(); // TODO 変更する
                  break;
                case 'datetime':
                  element = new KoyoriInputText(); // TODO 変更する
                  break;
                case 'tel':
                  element = new KoyoriInputText();
                  element.setAttribute('inputmode', 'tel');
                  if (!property.pattern) {
                    // TODO: 変更する
                  }
                  break;
                case 'email':
                  element = new KoyoriInputText();
                  element.setAttribute('inputmode', 'email');
                  if (!property.pattern) {
                    // TODO: 変更する
                  }
                  break;
                case 'url':
                  element = new KoyoriInputText();
                  element.setAttribute('inputmode', 'url');
                  if (!property.pattern) {
                    // TODO: 変更する
                  }
                  break;
              }
              if (property.references) {
                let datalist = document.createElement('datalist');
                let id = 'koy-form-datalist-' + KoyoriForm.listCount++;
                datalist.setAttribute('id', id);
                property.references.forEach((reference) => {
                  let option = document.createElement('option');
                  option.innerText = reference;
                  datalist.appendChild(option);
                });
                this.appendChild(datalist);

                element.setAttribute('autocomplete', '');
                element.setAttribute('list', id);
              }
            }
            if (property.maxLength != undefined) {
              element.setAttribute('maxlength', String(property.maxLength));
            }
            if (property.type == 'secret') {
              element.setAttribute('secret', '');
            }
            break;
          case 'boolean':
            element = new KoyoriCheckbox();
            element.setAttribute('value', 'true');
            break;
          case 'file':
            element = new KoyoriInputFile();
            break;
          default:
            let childType = KoyoriForm.getTypeByName(
              this.def.types,
              property.type
            );
            if (childType == null) {
              console.log('サポート外の型が定義されています: ' + property.type);
            } else {
              let fieldset = document.createElement('fieldset');
              let legend = document.createElement('legend');
              legend.textContent = property.label;
              fieldset.appendChild(legend);
              fieldset.classList.add('koy-form-group');
              parent.appendChild(fieldset);
              this.addElement(fieldset, name + '.', childType);
            }
            return;
        }
        if (element != null) {
          element.setAttribute('name', name);
          if (property.label != undefined) {
            element.setAttribute('label', property.label);
          }
        }
      }
      if (element != null) {
        element.removeAttribute('disabled');
        if (property.readOnly == true) {
          element.setAttribute('disabled', '');
        } else if (
          property.insertOnly == true &&
          this.getAttribute('insert-only-disable') != null
        ) {
          element.setAttribute('disabled', '');
        }
        if (property.value != undefined) {
          element.setAttribute('value', property.value);
        }
        element.addEventListener('change', () => {
          this.updateState();
        });
        parent.appendChild(element);
      }
    });
  }

  /**
   * フォーム内の入力エレメントの状態を更新します。
   */
  protected updateState(): void {
    KoyoriForm.getTypeByName(this.def.types, this.def.type).properties.forEach(
      (property) => {
        let element = <HTMLElement>(
          this.querySelector('[name="' + property.name + '"]')
        );
        if (element == null) {
          return;
        }

        if (property.enable != undefined && property.enable != null) {
          if (property.enable == 'true') {
            element.removeAttribute('disabled');
          } else if (property.enable == 'false') {
            element.setAttribute('disabled', '');
          } else if (this.isMatched(property.enable)) {
            element.removeAttribute('disabled');
          } else {
            element.setAttribute('disabled', '');
          }
        }

        if (property.visible != undefined && property.visible != null) {
          if (property.visible == 'true') {
            element.style.display = '';
          } else if (property.visible == 'false') {
            element.style.display = 'none';
          } else if (this.isMatched(property.visible)) {
            KoyoriAnimation.fadeIn(element);
          } else {
            KoyoriAnimation.fadeOut(element);
          }
        }
      }
    );
  }

  /**
   * name:value形式の式に一致するかどうかを判定します。
   *
   * @param pair name:value形式の式
   */
  private isMatched(pair: string) {
    let point = pair.indexOf(':');
    if (point <= 0) {
      return false;
    }
    let targetName = pair.substring(0, point);
    let targetValue = pair.substring(point + 1);
    let target = this.querySelector('[name="' + targetName + '"]');
    return (<KoyoriInput>target).value == targetValue;
  }

  /**
   * フォーム内の入力エレメントの幅を調整します。
   **/
  private updateLabelWidth(): void {
    let maxWidth = 0;
    let flexWrap: string = null;
    KoyoriElement.querySelectorAll(
      this.innerForm,
      '[name] > label > .koy-input-label'
    ).forEach((element) => {
      if (
        element.closest('koy-radio') != null ||
        element.closest('koy-checkbox') != null
      ) {
        return;
      }
      element.style.minWidth = '';
      let currentWidth = KoyoriElement.getOuterWidth(element);
      if (maxWidth < currentWidth) {
        maxWidth = currentWidth;
        let style = getComputedStyle(element.parentElement);
        flexWrap = style.flexWrap;
      }
    });
    KoyoriElement.querySelectorAll(
      this.innerForm,
      '[name] > label > .koy-input-label'
    ).forEach((element) => {
      if (
        element.closest('koy-radio') != null ||
        element.closest('koy-checkbox') != null
      ) {
        return;
      }
      element.style.boxSizing = 'border-box';
      element.style.minWidth = maxWidth + 'px';
    });
    KoyoriElement.querySelectorAll(
      this.innerForm,
      'koy-radio, koy-checkbox'
    ).forEach((element) => {
      if (element.closest('fieldset') != null) {
        return;
      }
      if (flexWrap == 'nowrap' && maxWidth > 0) {
        element.style.marginLeft = maxWidth + 'px';
      } else {
        element.style.marginLeft = '';
      }
    });
    KoyoriElement.querySelectorAll(
      this.innerForm,
      'koy-button.koy-form-default:not([name])'
    ).forEach((element, index) => {
      let style = getComputedStyle(element);
      if (index == 0 || style.display == 'block') {
        if (flexWrap == 'nowrap' && maxWidth > 0) {
          element.style.marginLeft = maxWidth + 'px';
        } else {
          element.style.marginLeft = '';
        }
      }
    });
  }
}
window.customElements.define('koy-form', KoyoriForm);

/**
 * 管理対象の定義です。
 */
interface KoyoriFormDefinition {
  /** オブジェクトの型名 */
  type: string;

  /** 構造データで使用される型 */
  types: KoyoriFormDefinitionType[];
}

/**
 * 型情報を定義します。
 */
interface KoyoriFormDefinitionType {
  /** 型名 */
  name: string;

  /** プロパティのリスト */
  properties: KoyoriFormDefinitionTypeProperty[];
}

/**
 * 型の１つのプロパティを定義します。
 */
interface KoyoriFormDefinitionTypeProperty {
  /** プロパティ名 */
  name: string;

  /** プロパティの表示名 */
  label: string;

  /** 型 */
  type: string;

  /** 追加時のみ設定可能の場合はtrue */
  insertOnly: boolean;

  /** 設定不可の場合はtrue */
  readOnly: boolean;

  /** 複数行入力が可能な場合はtrue */
  multiLine: boolean;

  /** デフォルト値 */
  value: string;

  /** 最大文字数もしくは最大ファイルサイズ */
  maxLength: number;

  /** 最小文字数もしくは最小ファイルサイズ */
  minLength: number;

  /** 最大値 */
  maxValue: number;

  /** 最小値 */
  minValue: number;

  /** 正規表現 */
  pattern: string;

  /** forward: 前方一致, backward: 後方一致, partial: 部分一致, 省略: 完全一致 */
  match: string;

  /** 選択候補のリスト */
  candidates: KoyoriFormDefinitionCandidate[];

  /** 入力候補のリスト */
  references: string[];

  /** 有効化条件（true、false、項目名:正規表現） */
  enable: string;

  /** 表示条件（true、false、項目名:正規表現） */
  visible: string;
}

/**
 * 選択候補を定義します。
 */
interface KoyoriFormDefinitionCandidate {
  /** 選択候補の表示 */
  label: string;

  /** 選択値 */
  value: string;
}

/**
 * 連想配列を格納します。
 */
interface KoyoriFormHash {
  [key: string]: any;
}
